import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseApi, Category } from '../services/supabase';
import { createClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG } from '../config/supabase';
import { 
  FolderOpen, 
  ArrowLeft, 
  Save, 
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import styled from 'styled-components';

const AddProjectContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
`;

const Form = styled.form`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  min-height: 48px;
  background: white;
`;

const Tag = styled.span`
  background: #e0e7ff;
  color: #3730a3;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveTag = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6366f1;
  padding: 0;
  
  &:hover {
    color: #dc2626;
  }
`;

const TagInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  flex: 1;
  min-width: 120px;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 20px;
`;

const ImageUploadArea = styled.div<{ hasImage: boolean }>`
  width: 100%;
  height: 200px;
  border: 2px dashed ${props => props.hasImage ? '#667eea' : '#e2e8f0'};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasImage ? '#f0f4ff' : 'white'};
  
  &:hover {
    border-color: #667eea;
    background: #f0f4ff;
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const ImagePreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageUploadOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${ImageUploadArea}:hover & {
    opacity: 1;
  }
`;

const ImageUploadText = styled.div`
  text-align: center;
  color: #64748b;
`;

const ImageUploadIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 16px;
  color: #667eea;
`;

const ImageUploadButton = styled.div`
  background: #667eea;
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 12px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImageInfo = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: #64748b;
  text-align: center;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(220, 38, 38, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(220, 38, 38, 1);
    transform: scale(1.1);
  }
`;



const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.variant === 'secondary' ? '#e2e8f0' : 'transparent'};
  background: ${props => props.variant === 'secondary' ? 'white' : '#667eea'};
  color: ${props => props.variant === 'secondary' ? '#374151' : 'white'};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const AddProject: React.FC = () => {
  const navigate = useNavigate();
  
  // Initialize Supabase client for storage using existing config
  const supabase = createClient(
    SUPABASE_CONFIG.URL,
    SUPABASE_CONFIG.ANON_KEY
  );
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    category_id: '',
    image_url: '',
    technologies: [] as string[],
    features: [] as string[],
    client: '',
    duration: '',
    budget: '',
    status: 'completed',
    project_url: '',
    github_url: '',
    demo_url: ''
  });

  const [techInput, setTechInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await supabaseApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = (type: 'technologies' | 'features', value: string, setInput: React.Dispatch<React.SetStateAction<string>>) => {
    if (value.trim() && !formData[type].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [type]: [...prev[type], value.trim()]
      }));
      setInput('');
    }
  };

  const removeTag = (type: 'technologies' | 'features', index: number) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const uploadImageToSupabase = async (): Promise<string | null> => {
    if (!selectedImage) return null;
    
    try {
      setUploading(true);
      console.log('Starting image upload...');
      
      // Create a unique filename
      const fileExt = selectedImage.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      console.log('Filename:', fileName);
      
      // Upload to Supabase Storage
      console.log('Uploading to bucket: images');
      const { data, error } = await supabase.storage
        .from('images')
        .upload(fileName, selectedImage);
      
      if (error) {
        console.error('Upload error details:', error);
        alert(`Failed to upload image: ${error.message}`);
        return null;
      }
      
      console.log('Upload successful, data:', data);
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(fileName);
      
      console.log('Public URL:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload image first if selected
      let imageUrl = formData.image_url;
      if (selectedImage) {
        const uploadedUrl = await uploadImageToSupabase();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          setLoading(false);
          return;
        }
      }

      // Create project with image URL
      const projectData = {
        ...formData,
        image_url: imageUrl
      };

      const success = await supabaseApi.createProject(projectData);
      if (success) {
        navigate('/projects');
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AddProjectContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem onClick={() => navigate('/')}>
            <FolderOpen size={20} />
            Dashboard
          </NavItem>
          <NavItem onClick={() => navigate('/projects')}>
            <FolderOpen size={20} />
            Projects
          </NavItem>
          <NavItem onClick={() => navigate('/categories')}>
            <FolderOpen size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageHeader>
            <BackButton onClick={() => navigate('/projects')}>
              <ArrowLeft size={20} />
            </BackButton>
            <PageTitle>Add New Project</PageTitle>
          </PageHeader>

          <Form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Project Title *</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                    placeholder="Enter project title"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Client</Label>
                  <Input
                    type="text"
                    value={formData.client}
                    onChange={(e) => handleInputChange('client', e.target.value)}
                    placeholder="Enter client name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="completed">Completed</option>
                    <option value="in-progress">In Progress</option>
                    <option value="planning">Planning</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Duration</Label>
                  <Input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="e.g., 3 months"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Budget</Label>
                  <Input
                    type="text"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    placeholder="e.g., $15,000"
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Description</SectionTitle>
              <FormGrid>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Short Description</Label>
                  <Textarea
                    value={formData.short_description}
                    onChange={(e) => handleInputChange('short_description', e.target.value)}
                    placeholder="Brief description (max 300 characters)"
                    maxLength={300}
                  />
                </FormGroup>

                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Full Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    required
                    placeholder="Detailed project description"
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Technologies & Features</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Technologies</Label>
                  <TagsContainer>
                    {formData.technologies.map((tech, index) => (
                      <Tag key={index}>
                        {tech}
                        <RemoveTag onClick={() => removeTag('technologies', index)}>
                          <X size={14} />
                        </RemoveTag>
                      </Tag>
                    ))}
                    <TagInput
                      type="text"
                      value={techInput}
                      onChange={(e) => setTechInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag('technologies', techInput, setTechInput)}
                      placeholder="Add technology..."
                    />
                  </TagsContainer>
                </FormGroup>

                <FormGroup>
                  <Label>Features</Label>
                  <TagsContainer>
                    {formData.features.map((feature, index) => (
                      <Tag key={index}>
                        {feature}
                        <RemoveTag onClick={() => removeTag('features', index)}>
                          <X size={14} />
                        </RemoveTag>
                      </Tag>
                    ))}
                    <TagInput
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag('features', featureInput, setFeatureInput)}
                      placeholder="Add feature..."
                    />
                  </TagsContainer>
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Media & Links</SectionTitle>
              <FormGrid>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Project Image</Label>
                  <ImageUploadSection>
                    <ImageUploadArea 
                      hasImage={!!imagePreview || !!formData.image_url}
                      onClick={() => document.getElementById('image-upload')?.click()}
                    >
                      {imagePreview ? (
                        <ImagePreview>
                          <ImagePreviewImg src={imagePreview} alt="Preview" />
                          <ImageUploadOverlay>
                            Click to change image
                          </ImageUploadOverlay>
                          <RemoveImageButton onClick={(e) => { e.stopPropagation(); removeImage(); }}>
                            <X size={16} />
                          </RemoveImageButton>
                        </ImagePreview>
                      ) : formData.image_url ? (
                        <ImagePreview>
                          <ImagePreviewImg src={formData.image_url} alt="Current" />
                          <ImageUploadOverlay>
                            Click to change image
                          </ImageUploadOverlay>
                          <RemoveImageButton onClick={(e) => { e.stopPropagation(); removeImage(); }}>
                            <X size={16} />
                          </RemoveImageButton>
                        </ImagePreview>
                      ) : (
                        <>
                          <ImageUploadIcon>📷</ImageUploadIcon>
                          <ImageUploadText>
                            Click to upload image or drag and drop
                          </ImageUploadText>
                          <ImageUploadButton>
                            <Upload size={16} style={{ marginRight: '8px' }} />
                            Upload Image
                          </ImageUploadButton>
                        </>
                      )}
                    </ImageUploadArea>
                    
                    <HiddenFileInput
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                    />
                    
                    <ImageInfo>
                      Supported formats: JPG, PNG, GIF • Max size: 5MB
                    </ImageInfo>
                    
                    {!imagePreview && !formData.image_url && (
                      <div style={{ marginTop: '12px' }}>
                        <Label>Or enter image URL:</Label>
                        <Input
                          type="url"
                          value={formData.image_url}
                          onChange={(e) => handleInputChange('image_url', e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          style={{ marginTop: '8px' }}
                        />
                      </div>
                    )}
                  </ImageUploadSection>
                </FormGroup>

                <FormGroup>
                  <Label>Project URL</Label>
                  <Input
                    type="url"
                    value={formData.project_url}
                    onChange={(e) => handleInputChange('project_url', e.target.value)}
                    placeholder="https://project-demo.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>GitHub Repository</Label>
                  <Input
                    type="url"
                    value={formData.github_url}
                    onChange={(e) => handleInputChange('github_url', e.target.value)}
                    placeholder="https://github.com/username/repo"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Demo URL</Label>
                  <Input
                    type="url"
                    value={formData.demo_url}
                    onChange={(e) => handleInputChange('demo_url', e.target.value)}
                    placeholder="https://demo.project.com"
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/projects')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : (
                  <>
                    <Save size={16} style={{ marginRight: '8px' }} />
                    Create Project
                  </>
                )}
              </Button>
            </FormActions>
          </Form>
        </Content>
      </MainContent>
    </AddProjectContainer>
  );
};

export default AddProject;
